import type {
  CompositeSubField,
  CompositeSubFieldValue,
} from '~/api/sylvanus/typedefs.autogen';
import type { Highlight } from '~/features/document-viewer/types/highlight';

import { formatAddress } from '~/features/unified-fields/read-components/formatAddress';

import {
  isCompositeValueRecord,
  readFieldComposite,
  readSubfieldScalar,
  subTypeFor,
  type StoredFieldComposite,
} from './monetaryComposite';

export const ADDRESS_TEMPLATE_ID = 'address';

export type AddressCompositeConfig = {
  templateId: typeof ADDRESS_TEMPLATE_ID;
  fieldDescription: string;
  subfields: CompositeSubField[];
};

export function createDefaultAddressComposite(
  fieldDescription: string,
): AddressCompositeConfig {
  return {
    templateId: ADDRESS_TEMPLATE_ID,
    fieldDescription,
    subfields: [
      {
        name: 'address_lines',
        sub_type: 'string',
        description: 'Street address line',
        required: true,
      },
      {
        name: 'locality',
        sub_type: 'string',
        description: 'City or locality',
        required: false,
      },
      {
        name: 'administrative_area',
        sub_type: 'string',
        description: 'State, province, or region',
        required: false,
      },
      {
        name: 'postal_code',
        sub_type: 'string',
        description: 'Postal or ZIP code',
        required: false,
      },
      {
        name: 'country',
        sub_type: 'string',
        description: 'Country or region',
        required: false,
      },
    ],
  };
}

export function readAddressComposite(
  config: StoredFieldComposite,
): AddressCompositeConfig | undefined {
  return readFieldComposite(config, ADDRESS_TEMPLATE_ID);
}

const ADDRESS_LINES = 'address_lines';
const LOCALITY = 'locality';
const ADMINISTRATIVE_AREA = 'administrative_area';
const POSTAL_CODE = 'postal_code';
const COUNTRY = 'country';

export type AddressCompositeParts = {
  addressLine: string;
  locality: string;
  administrativeArea: string;
  postalCode: string;
  country: string;
};

const EMPTY_ADDRESS_PARTS: AddressCompositeParts = {
  addressLine: '',
  locality: '',
  administrativeArea: '',
  postalCode: '',
  country: '',
};

export function resolveAddressComposite(
  composite: AddressCompositeConfig | undefined,
): AddressCompositeConfig {
  return composite ?? createDefaultAddressComposite('');
}

export function formatAddressCompositeValue(value: unknown): string {
  const parts = readAddressCompositeValue(value);
  return formatAddress({
    address_lines: parts.addressLine ? [parts.addressLine] : [],
    locality: parts.locality,
    administrative_area: parts.administrativeArea,
    postal_code: parts.postalCode,
    country: parts.country,
  });
}

export function readAddressCompositeValue(
  value: unknown,
): AddressCompositeParts {
  if (!isCompositeValueRecord(value)) {
    return EMPTY_ADDRESS_PARTS;
  }

  return {
    addressLine: readSubfieldScalar(value[ADDRESS_LINES]),
    locality: readSubfieldScalar(value[LOCALITY]),
    administrativeArea: readSubfieldScalar(value[ADMINISTRATIVE_AREA]),
    postalCode: readSubfieldScalar(value[POSTAL_CODE]),
    country: readSubfieldScalar(value[COUNTRY]),
  };
}

export function buildAddressCompositeValue(
  parts: AddressCompositeParts,
  subfields: CompositeSubField[],
): { [key: string]: CompositeSubFieldValue } {
  return {
    [ADDRESS_LINES]: subfieldValue(
      ADDRESS_LINES,
      subTypeFor(subfields, ADDRESS_LINES, 'string'),
      parts.addressLine,
    ),
    [LOCALITY]: subfieldValue(
      LOCALITY,
      subTypeFor(subfields, LOCALITY, 'string'),
      parts.locality,
    ),
    [ADMINISTRATIVE_AREA]: subfieldValue(
      ADMINISTRATIVE_AREA,
      subTypeFor(subfields, ADMINISTRATIVE_AREA, 'string'),
      parts.administrativeArea,
    ),
    [POSTAL_CODE]: subfieldValue(
      POSTAL_CODE,
      subTypeFor(subfields, POSTAL_CODE, 'string'),
      parts.postalCode,
    ),
    [COUNTRY]: subfieldValue(
      COUNTRY,
      subTypeFor(subfields, COUNTRY, 'string'),
      parts.country,
    ),
  };
}

function subfieldValue(
  name: string,
  subType: CompositeSubField['sub_type'],
  value: string,
): CompositeSubFieldValue {
  return {
    name,
    sub_type: subType,
    value: value === '' ? null : value,
  };
}

function isEmptyAddress(parts: AddressCompositeParts) {
  return (
    parts.addressLine === '' &&
    parts.locality === '' &&
    parts.administrativeArea === '' &&
    parts.postalCode === '' &&
    parts.country === ''
  );
}

export function toAddressGoldValue({
  absent = false,
  composite,
  highlights,
  parts,
}: {
  absent?: boolean;
  composite: AddressCompositeConfig | undefined;
  highlights: Highlight[] | undefined;
  parts?: AddressCompositeParts;
}) {
  const resolved = resolveAddressComposite(composite);
  const nextParts = parts ?? EMPTY_ADDRESS_PARTS;
  if (!absent && isEmptyAddress(nextParts)) {
    if (!highlights?.length) {
      return null;
    }
    return {
      highlights,
      type: 'unknown' as const,
      value: null,
    };
  }

  return {
    highlights,
    subfields: resolved.subfields,
    templateId: resolved.templateId,
    type: 'composite' as const,
    value: absent
      ? null
      : buildAddressCompositeValue(nextParts, resolved.subfields),
  };
}
